import {Toast} from "antd-mobile";
import {TimeModel} from "../typings/time_model";

/**
 * 从当前页 URL 读取 query 参数。
 * 兼容小程序 web-view 将 `&` 写成 `%26` 时 `URLSearchParams` 无法解析的情况。
 *
 * @param param 参数名
 */
export function getUrlParam(param: string): string {
  const tryParse = (search: string): string => {
    if (!search) {
      return '';
    }
    const normalized = search.startsWith('?') ? search : `?${search}`;
    const qs = normalized.replace(/%26/gi, '&');
    return new URLSearchParams(qs).get(param) || '';
  };

  const fromSearch = tryParse(window.location.search);
  if (fromSearch) {
    return fromSearch;
  }

  const href = window.location.href;
  const qIndex = href.indexOf('?');
  if (qIndex < 0) {
    return '';
  }
  const hashIndex = href.indexOf('#', qIndex);
  const rawQs = hashIndex >= 0 ? href.slice(qIndex, hashIndex) : href.slice(qIndex);
  return tryParse(rawQs);
}

/**
 * 与微信小程序宿主（xiaosimen `webviewAuth`）约定的 query 名：出现且为真值时表示当前页由 `<web-view>` 打开。
 */
export const IN_WX_QUERY_KEY = 'inWx';

/**
 * 与 {@link IN_WX_QUERY_KEY} 配对的约定取值（忽略大小写时 `true` 亦视为真）。
 */
export const IN_WX_QUERY_VALUE = '1';

/**
 * 从当前页 URL query 判断是否带有「微信小程序 WebView」宿主标记。
 * 供各业务页与 `EnvUtils.isWxMiniProgramWebview` 组合判断复用，避免各处硬编码字符串。
 *
 * @returns 当 `inWx` 为 `1` 或 `true`（忽略大小写）时为 true
 */
export function isInWxFromUrlQuery(): boolean {
  const v = getUrlParam(IN_WX_QUERY_KEY).trim().toLowerCase();
  return v === IN_WX_QUERY_VALUE || v === 'true';
}

export function objectFlat<T>(allCheckedList: { [key: string]: T[] }): T[] {
  let flatList: T[] = [];
  // 遍历对象，将值取出来，合并成一个数组
  for (const key in allCheckedList) {
    if (Object.prototype.hasOwnProperty.call(allCheckedList, key)) {
      const element = allCheckedList[key];
      flatList = flatList.concat(element);
    }
  }
  return flatList;
}

export function isNotEmptyStr(s:any) {
  if (typeof s == 'string' && s.length > 0) {
    return true
  }
  return false
}

export function isEmptyStr(s:any) {
  return !isNotEmptyStr(s);
}

function copyToClipboardCommand(shareContent:any):boolean{
  const textArea = document.createElement('textarea');
  document.body.appendChild(textArea);
  textArea.textContent = shareContent;
  textArea.select();
  try {
    const isOk = document.execCommand('copy');
    if(isOk){
      Toast.show({content:`${shareContent}`});
      return true;
    }else{
      Toast.show({content:`复制失败，请尝试手动复制`});
      return false;
    }
  } catch (err) {
    Toast.show({content:`复制失败，请尝试手动复制`});
    return false;
  }
  finally {
    document.body.removeChild(textArea);
  }
}

export async function copyToClipboard(shareContent:any):Promise<boolean> {
  if(window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(shareContent);
      Toast.show({content: `${shareContent}`});
      return true;
    } catch (err) {
      return copyToClipboardCommand(shareContent);;
    }
  }else{
    return copyToClipboardCommand(shareContent);
  }
}

/**
 * 同步复制纯文本。微信 / App 内嵌 WebView 中 {@link navigator.clipboard.writeText} 常无异常但剪贴板为空；
 * 在用户点击的同步调用栈内使用 {@link document.execCommand}('copy') 更可靠。
 */
export function copyPlainTextSync(text: string): boolean {
  const t = String(text ?? '');
  if (!t) {
    return false;
  }
  const textArea = document.createElement('textarea');
  textArea.value = t;
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  textArea.style.top = '0';
  textArea.style.opacity = '0';
  textArea.setAttribute('readonly', 'readonly');
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  textArea.setSelectionRange(0, t.length);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  } finally {
    document.body.removeChild(textArea);
  }
  return ok;
}

/** 优先同步复制（保留用户手势）；失败时再尝试 Clipboard API。 */
export async function copyPlainText(text: string): Promise<boolean> {
  const t = String(text ?? '');
  if (!t.trim()) {
    return false;
  }
  if (copyPlainTextSync(t)) {
    return true;
  }
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(t);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

export function buildGroupPurchaseShareContent(groupNo: string, goodsName?: string): string {
  if (!groupNo) return '';
  const title = '英语学习宝藏App天才鸭';
  const shareUrl = `${window.location.origin}/group_share?groupNo=${groupNo}`;
  return `【${title}】#>${groupNo}<# 点击【${shareUrl}】下载安装后在App内输入拼团码即可参与拼团！`;
}

export function formatAges(str: string) {
  if(!str.includes(",")){
    return str.trim();
  }
  // 分割字符串为数组
  const numbers = str.split(',').map(Number);

  // 找到最小值和最大值
  const min = Math.min(...numbers);
  const max = Math.max(...numbers);

  // 构建结果字符串
  return `${min}~${max}`;
}

export async function share({title,text,url}:Record<string, any>):Promise<boolean> {
  try {
    await navigator.share({
      title: title,
      text: text,
      url: url,
    });
    return true;
  } catch (error) {
    return false;
  }
}

export function timeDifference(expireTimeString?:string):TimeModel|undefined{

  if(!expireTimeString){
    return undefined;
  }

  let timeModel:TimeModel|undefined;
  const expireTime:Date = new Date(expireTimeString.replace(' ', 'T') + 'Z');
  const now:Date = new Date();
  const difference:number = expireTime.getTime() - now.getTime();

  if (difference > 0) {
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    timeModel = { days, hours, minutes, seconds };
  }
  return timeModel;
}

// utils/dateUtils.ts
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return ""; // 如果日期不存在，返回空字符串

  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 月份从 0 开始，需要加 1
  const day = date.getDate();

  return `${year}.${month}.${day}`;
};

export function formatStorageSize(sizeInMB: number): string {
  if (sizeInMB < 0) {
    return '0 MB';
  }

  const formatNumber = new Intl.NumberFormat('en', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format;

  if (sizeInMB >= 1024 * 1024) {
    return `${formatNumber(sizeInMB / (1024 * 1024))} TB`;
  }
  if (sizeInMB >= 1024) {
    return `${formatNumber(sizeInMB / 1024)} GB`;
  }
  return `${formatNumber(sizeInMB)} MB`;
}

/**
 * 全局内存存储（一级缓存）
 * 作为快速访问的缓存层，优先从内存读取
 */
const memoryStorage: Record<string, string> = {};

/**
 * 检查 localStorage 是否可用
 * 在某些情况下 localStorage 可能不可用：
 * - Safari 隐私浏览模式
 * - 存储配额已满
 * - 用户禁用存储
 * - 某些 WebView 环境限制
 */
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    console.warn('[Storage] localStorage 不可用，将仅使用内存存储:', e);
    return false;
  }
}

/**
 * 安全的存储读取
 * 缓存策略：一级缓存（内存） -> 二级缓存（localStorage） -> 默认值
 * 
 * @param key 存储键名
 * @param defaultValue 默认值
 * @returns 存储的值或默认值
 */
export function safeLocalStorageGet(key: string, defaultValue: string | null = null): string | null {
  // 一级缓存：优先从内存读取（最快）
  if (key in memoryStorage) {
    return memoryStorage[key];
  }
  
  // 二级缓存：从 localStorage 读取
  if (isLocalStorageAvailable()) {
    try {
      const value = localStorage.getItem(key);
      if (value !== null) {
        // 同步到一级缓存，下次读取更快
        memoryStorage[key] = value;
        return value;
      }
    } catch (e) {
      console.warn(`[Storage] 从 localStorage 读取 ${key} 失败:`, e);
    }
  }
  
  // 都没有，返回默认值
  return defaultValue;
}

/**
 * 安全的存储写入
 * 缓存策略：同时写入一级缓存（内存）和二级缓存（localStorage）
 * 
 * @param key 存储键名
 * @param value 存储的值
 * @returns 是否写入成功（至少写入内存成功即返回 true）
 */
export function safeLocalStorageSet(key: string, value: string): boolean {
  // 一级缓存：先写入内存（最快，保证可用性）
  memoryStorage[key] = value;
  
  // 二级缓存：写入 localStorage（持久化）
  if (isLocalStorageAvailable()) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      // 可能是存储配额已满或其他错误
      if (e instanceof DOMException && (e.code === 22 || e.code === 1014 || e.name === 'QuotaExceededError')) {
        console.warn(`[Storage] localStorage 存储配额已满，数据已保存到内存缓存:`, key);
      } else {
        console.warn(`[Storage] 保存到 localStorage 失败，数据已保存到内存缓存:`, key, e);
      }
      // 即使 localStorage 失败，内存缓存已写入，返回 true
      return true;
    }
  }
  
  // localStorage 不可用，但内存缓存已写入，返回 true
  return true;
}

/**
 * 安全的存储删除
 * 同时从一级缓存（内存）和二级缓存（localStorage）中删除
 * 
 * @param key 存储键名
 * @returns 是否删除成功（至少从内存删除成功即返回 true）
 */
export function safeLocalStorageRemove(key: string): boolean {
  let deletedFromMemory = false;
  
  // 一级缓存：从内存删除
  if (key in memoryStorage) {
    delete memoryStorage[key];
    deletedFromMemory = true;
  }
  
  // 二级缓存：从 localStorage 删除
  if (isLocalStorageAvailable()) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`[Storage] 从 localStorage 删除 ${key} 失败:`, e);
    }
  }
  
  return deletedFromMemory;
}
